import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { Button, Col, Form } from 'react-bootstrap';
import { useFormikContext } from 'formik';
import moment from 'moment';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { MdDelete } from 'react-icons/md';
import CreatableSelect from 'react-select/creatable';
import SearchTemplateModal from '../components/searchTemplateModal';
import VisitsDymanicTable from './visits-dynamic-table';
import TemplateModel from '../components/templateModal';
import { getAllVisits, getDiagnosis, getDurations } from '../../../../redux/visit';
import { updateTemplateItems, getTemplates, createTemplate, getTemplateById } from '../../../../redux/template';
import useDebounce from '../../../../utils/useDebounceHook';
import { optionsStyle, visitInitialValues } from '../composables';

const DiagnosisDropdown = ({ options, value, onChange, onSearch }) => (
	<Form.Group as={Col}>
		<CreatableSelect
			menuPortalTarget={document.body}
			styles={optionsStyle}
			isClearable
			options={options}
			value={value}
			onChange={onChange}
			onInputChange={(inputValue, { action }) => {
				if (action === 'input-change') {
					onSearch(inputValue);
				}
			}}
			isValidNewOption={(inputValue, _selectValue, selectOptions) =>
				!!inputValue.trim() &&
				!selectOptions.some(
					(opt) => opt.label.toLowerCase() === inputValue.trim().toLowerCase()
				)
			}
			filterOption={(option, rawInput) =>
				(!value || value.label.toLowerCase() !== option.label.toLowerCase()) &&
				option.label.toLowerCase().includes(rawInput.toLowerCase())
			}
		/>
	</Form.Group>
);

const DateInput = ({ value, onChange }) => (
	<Form.Control
		type="date"
		className="editable-cell p-1 w-100"
		placeholder="DD-MM-YYYY"
		value={value || ''}
		format="YYYY-MM-DD"
		onChange={(e) => onChange(e.target.value)}
	/>
);

const Diagnosis = ({ customTitle, isHideIcons = false, onClose = () => { }, isFromSettings = false, search = '' }) => {
	const { values, setFieldValue, errors, touched, setFieldError, setFieldTouched } = useFormikContext();
	const [errorMessage, setErrorMessage] = useState([]);
	const [model, setModel] = useState(false);
	const { diagnosis, durations } = useSelector((state) => state.visits);
	const dispatch = useDispatch();
	const [searchTerms, setSearchTerms] = useState({
		diagnosis: '',
		duration: ''
	});
	const [diagnosisOptions, setDiagnosisOptions] = useState([]);
	const debouncedSearch = useDebounce(searchTerms, 500);
	const params = useParams();
	const [searchModel, setSearchModel] = useState(false);
	const { list: templates } = useSelector((state) => state.templates);
	const [durationOptions, setDurationOptions] = useState([]);

	useEffect(() => {
		if (isFromSettings) {
			setFieldValue('diagnosis', [...visitInitialValues.diagnosis]);
		}
	}, [isFromSettings]);

	const handleSelectTemplate = (option) => {
		dispatch(getTemplateById({ id: option.value })).unwrap().then(res => {
			setSearchModel(false);
			const fetchedDiagnosis = res.response.data.data.diagnosisItems;
			const newDiagnosis = fetchedDiagnosis.map((diagnosis, index) => ({
				id: index + 1,
				diagnosisId: diagnosis.diagnosis_id,
				durationId: diagnosis.duration_id,
				date: moment(diagnosis.date).format('YYYY-MM-DD')
			}));
			setFieldValue('diagnosis', newDiagnosis);
			if (
				newDiagnosis.length > 0 &&
				typeof errors.diagnosis === 'string'
			) {
				setFieldError('diagnosis', undefined);
				setFieldTouched('diagnosis', false);
			}
		});
	};

	const handleValueChange = (index, field, value, newValue) => {
		const updatedDiagnosis = values.diagnosis.map((item) => ({ ...item }));
		updatedDiagnosis[index][field] = value;
		if (field === 'diagnosisId') {
			updatedDiagnosis[index]['diagnosisName'] = newValue;
		}
		if (field === 'durationId') {
			updatedDiagnosis[index]['durationName'] = newValue;
		}
		setFieldValue('diagnosis', updatedDiagnosis);
	};

	const handleNewDiagnosis = (index, diagnosisName) => {
		const isDuplicate = diagnosisOptions.some(
			opt => opt.label.toLowerCase() === diagnosisName.toLowerCase()
		) || values.diagnosis.some(
			diagnosis => diagnosis.diagnosisName?.toLowerCase() === diagnosisName.toLowerCase()
		);
		if (isDuplicate) {
			toast.error('This diagnosis already exists or is selected.');
			return;
		}

		const tempId = `temp-${Date.now()}`;
		const newOption = { label: diagnosisName, value: tempId };
		setDiagnosisOptions((prev) => [...prev, newOption]);
		handleValueChange(index, 'diagnosisId', tempId, diagnosisName);
	};

	const handleNewDuration = (index, durationName) => {

		const isDuplicate = durationOptions.some(
			opt => opt.label.toLowerCase() === durationName.toLowerCase()
		) || values.diagnosis.some(
			diagnosis => diagnosis.durationName?.toLowerCase() === durationName.toLowerCase()
		);

		if (isDuplicate) {
			toast.error('This duration already exists or is selected.');
			return;
		}

		const tempId = `temp-${Date.now()}`;
		const newOption = { label: durationName, value: tempId };
		setDurationOptions(prev => [...prev, newOption]);
		handleValueChange(index, 'durationId', tempId, durationName);
	};

	const addNewRow = () => {
		const newRow = {
			id: values.diagnosis.length + 1,
			diagnosisId: '',
			diagnosisValue: '',
			duration: '',
			date: '',
		};
		setFieldValue('diagnosis', [...values.diagnosis, newRow]);
		setErrorMessage([]);
	};

	const handleDelete = (index) => {
		const updatedDiagnosis = values.diagnosis.filter((_, i) => i !== index);
		setFieldValue('diagnosis', updatedDiagnosis);
	};

	const handleSearchChange = (type) => (value) => {
		setSearchTerms((prev) => ({ ...prev, [type]: value }));
	};

	const mergeOptions = (setState) => (items) => {
		if (!items?.length) return;
		const newOptions = items.map(item => ({
			label: item.name,
			value: item.id
		}));
		setState(prevOptions => {
			const existingIds = new Set(prevOptions.map(opt => opt.value));
			const merged = [...prevOptions];
			newOptions.forEach(opt => {
				if (!existingIds.has(opt.value)) {
					merged.push(opt);
				}
			});
			return merged;
		});
	};

	const validateDiagnosis = () => {
		const errors = [];
		if (values.diagnosis.length === 0) {
			errors.push('Please add at least one diagnosis');
		} else {
			values.diagnosis.forEach((diagnose, index) => {
				if (!diagnose.diagnosisId) {
					errors.push(`Row ${index + 1}: Diagnosis is required`);
				}
				if (!diagnose.durationId) {
					errors.push(`Row ${index + 1}: Duration is required`);
				}
				if (!diagnose.date) {
					errors.push(`Row ${index + 1}: Date is required`);
				}
			});
		}
		if (errors.length > 0) {
			setErrorMessage(errors);
			return false;
		} else {
			setErrorMessage([]);
			return true;
		}
	};

	const saveTemplate = async() => {
		if (validateDiagnosis()) {
			const existingTemplate = values.diagnosis.find(diagnosis => diagnosis.existingTemplateId);
			const existingTemplateId = existingTemplate?.existingTemplateId;
			const payload = {
				templateId: existingTemplateId || null,
				diagnosis: values.diagnosis.map((diagnosis) => {
					let diagnosisData = {};

					if (
						typeof diagnosis.diagnosisId === 'string' && diagnosis.diagnosisId.startsWith('temp-')
					) {
						diagnosisData = { ...diagnosisData, diagnosisName: diagnosis.diagnosisValue };
					} else if (typeof diagnosis.diagnosisId === 'number') {
						diagnosisData = { ...diagnosisData, diagnosisId: diagnosis.diagnosisId };
					}
					if (typeof diagnosis.durationId === 'string' && diagnosis.durationId.startsWith('temp-')) {
						diagnosisData = { ...diagnosisData, durationName: diagnosis.durationName };
					} else if (typeof diagnosis.durationId === 'number') {
						diagnosisData = { ...diagnosisData, durationId: diagnosis.durationId };
					}
					return {
						...diagnosisData,
						id: diagnosis.existingTemplateItemId || null,
						date: diagnosis.date,
					};
				}),
			};
			if (existingTemplateId) {
				await dispatch(updateTemplateItems(payload)).unwrap().then(() => {
					dispatch(getTemplates({ type: 'diagnosis', search: search, per_page: 10, pId: Number(params.id) }));
					onClose();
				}).catch((err) => {
					toast.error(err.message || 'Something went wrong');
				});
			} else {
				setModel(true);
			}
		} else {
			return;
		}
	};

	const submitTemplate = async(name) => {
		const payload = {
			pId: Number(params.id),
			name: name,
			type: 'diagnosis',
			diagnosis: values.diagnosis.map((diagnosis) => {
				let diagnosisData = {};

				if (
					typeof diagnosis.diagnosisId === 'string' &&
					diagnosis.diagnosisId.startsWith('temp-')
				) {
					diagnosisData = { ...diagnosisData, diagnosisName: diagnosis.diagnosisValue };
				} else if (typeof diagnosis.diagnosisId === 'number') {
					diagnosisData = { ...diagnosisData, diagnosisId: diagnosis.diagnosisId };
				}
				if (typeof diagnosis.durationId === 'string' && diagnosis.durationId.startsWith('temp-')) {
					diagnosisData = { ...diagnosisData, durationName: diagnosis.durationName };
				} else if (typeof diagnosis.durationId === 'number') {
					diagnosisData = { ...diagnosisData, durationId: diagnosis.durationId };
				}
				return {
					...diagnosisData,
					date: diagnosis.date,
				};
			}),
		};
		await dispatch(createTemplate(payload)).unwrap().then(() => {
			dispatch(getTemplates({ type: 'diagnosis', search: search, per_page: 10, pId: Number(params.id) }));
			setModel(false);
			onClose();
		}).catch((err) => {
			toast.error(err.message || 'Something went wrong');
		});
	};

	const openSearchTemplate = () => {
		setSearchModel(true);
		dispatch(getTemplates({ type: 'diagnosis', search: search, per_page: 10, pId: Number(params.id) }));
	};

	const handleLoadLastVisit = () => {
		dispatch(getAllVisits({ pId: Number(params.id), type: 'all', page: 1, per_page: 1 })).unwrap().then(res => {
			setSearchModel(false);
			const visits = res.response.data.data.data;
			if (visits.length === 0) {
				return;
			}
			const fetchedDiagnosis = visits[0].diagnosis;
			const newDiagnosis = fetchedDiagnosis && fetchedDiagnosis.length > 0 ? fetchedDiagnosis.map((diagnosis, index) => ({
				id: index + 1,
				diagnosisId: diagnosis.diagnosis_id,
				durationId: diagnosis.duration_id,
				date: diagnosis.date ? moment(diagnosis.date).format('YYYY-MM-DD') : '',
			})) : visitInitialValues.diagnosis;
			setFieldValue('diagnosis', newDiagnosis);
			if (
				newDiagnosis.length > 0 &&
				typeof errors.diagnosis === 'string'
			) {
				setFieldError('diagnosis', undefined);
				setFieldTouched('diagnosis', false);
			}
		});
	};

	const columns = [
		{
			name: '#',
			selector: (_, index) => index + 1,
			width: '50px',
		},
		{
			name: t('VISIT.DIAGNOSIS_TITLE'),
			cell: (row, index) => {
				const selectedIds = values.diagnosis
					.filter((_, i) => i !== index)
					.map(c => c.diagnosisId);

				const filteredOptions = diagnosisOptions.filter(
					opt => !selectedIds.includes(opt.value)
				);
				return (
					<DiagnosisDropdown
						options={filteredOptions}
						value={filteredOptions.find((opt) => opt.value === row.diagnosisId) || ''}
						onChange={(option) => {
							if (option?.__isNew__) {
								handleNewDiagnosis(index, option.label);
							} else {
								handleValueChange(index, 'diagnosisId', option?.value || '');
							}
						}}
						onSearch={handleSearchChange('diagnosis')}
					/>
				);
			},
			width: '300px',
		},
		{
			name: t('VISIT.DURATION'),
			cell: (row, index) => {
				return (
					<DiagnosisDropdown
						options={durationOptions}
						value={durationOptions.find(opt => opt.value === row.durationId) || ''}
						onChange={(option) => {
							if (option?.__isNew__) {
								handleNewDuration(index, option.label);
							} else {
								handleValueChange(index, 'durationId', option?.value || '');
							}
						}}
						onSearch={handleSearchChange('duration')}
					/>
				);
			},
			width: '200px',
		},
		{
			name: t('VISIT.DATE'),
			cell: (row, index) => (
				<DateInput
					value={row.date}
					onChange={(value) => handleValueChange(index, 'date', value)}
				/>
			),
		},
		{
			name: t('CFORMS.COLUMNS.ACTIONS'),
			cell: (_row, index) => (
				<Button
					title={t('GENERAL.DELETE')}
					size="sm"
					variant="danger"
					onClick={() => handleDelete(index)}
				>
					<MdDelete size={18} />
				</Button>
			),
			width: '80px',
		},
	];

	useEffect(() => {
		mergeOptions(setDiagnosisOptions)(diagnosis);
	}, [diagnosis]);

	useEffect(() => {
		mergeOptions(setDurationOptions)(durations);
	}, [durations]);

	useEffect(() => {
		const { diagnosis, duration } = debouncedSearch;
		if (diagnosis) {
			dispatch(getDiagnosis({ search: debouncedSearch, per_page: 5, status: 'active' }));
		}
		if (duration) {
			dispatch(getDurations({ search: debouncedSearch, per_page: 5, status: 'active' }));
		}
	}, [debouncedSearch, dispatch]);

	return (
		<>
			<VisitsDymanicTable
				title={customTitle}
				columns={columns}
				data={values.diagnosis || []}
				addRow={addNewRow}
				saveTemplate={saveTemplate}
				searchTemplate={openSearchTemplate}
				errorMessage={errorMessage}
				handleDelete={handleDelete}
				loadLastVisit={handleLoadLastVisit}
				isHideIcons={isHideIcons}
			/>
			<TemplateModel
				show={model}
				close={() => setModel(false)}
				submitTemplate={submitTemplate}
			/>
			<SearchTemplateModal
				show={searchModel}
				close={() => setSearchModel(false)}
				type="Diagnosis"
				options={templates}
				handleSelectTemplate={handleSelectTemplate}
				pId={Number(params.id)}
				isFromSettings={isFromSettings}
			/>
			{typeof errors.diagnosis === 'string' && touched.diagnosis && (
				<Form.Text className="text-danger mb-2 d-block">
					{errors.diagnosis}
				</Form.Text>
			)}
		</>
	);
};

export default Diagnosis;
